import React, { useState, useEffect, useCallback } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import BottomButtonGroup, { BUTTON_LOADING_ICON, FORM_INLINE_ACTION_BUTTON_SX } from '../../component/BottomButtonGroup'
import { getLocationState } from '../../utils/listPageState'

const DEFAULT_APP_MODE = 'rm'
const COUPANG_APP_MODE = 'coupang'
const DEFAULT_GIFTICON_FILTER_SETTING = {
    appMode: DEFAULT_APP_MODE,
    minPrice: 3000,
    maxPrice: 29000,
}

const buildDefaultGifticonFilterSetting = (appMode = DEFAULT_APP_MODE) => ({
    ...DEFAULT_GIFTICON_FILTER_SETTING,
    appMode,
})

export default function Settings({ role }) {
    const location = useLocation()
    const history = useHistory()
    const locationState = getLocationState(location)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [hasDetail, setHasDetail] = useState(false)
    const [openSnackbar, setOpenSnackbar] = useState(locationState.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(locationState.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(locationState.messageSnackbar)
    const [params, setParams] = useState({})
    const [tempParams, setTempParams] = useState({})
    const [gifticonFilterSetting, setGifticonFilterSetting] = useState(() => buildDefaultGifticonFilterSetting(DEFAULT_APP_MODE))
    const [tempGifticonFilterSetting, setTempGifticonFilterSetting] = useState(() => buildDefaultGifticonFilterSetting(DEFAULT_APP_MODE))
    const [coupangGifticonFilterSetting, setCoupangGifticonFilterSetting] = useState(() => buildDefaultGifticonFilterSetting(COUPANG_APP_MODE))
    const [tempCoupangGifticonFilterSetting, setTempCoupangGifticonFilterSetting] = useState(() => buildDefaultGifticonFilterSetting(COUPANG_APP_MODE))
    const [editUrlMode, setEditUrlMode] = useState(false)
    const [editGifticonFilterMode, setEditGifticonFilterMode] = useState(false)
    const [editCoupangGifticonFilterMode, setEditCoupangGifticonFilterMode] = useState(false)
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    // const [editNextItemMode, setEditNextItemMode] = useState(false) // 앞으로 추가 설정들 하나씩 수정해서 사용하자

    // 설정 조회 실패 시 편집 상태를 닫고 이전 설정값이 남아 보이지 않게 초기화한다.
    const resetDetailState = useCallback((errorMessage = '') => {
        setParams({})
        setTempParams({})
        setGifticonFilterSetting(buildDefaultGifticonFilterSetting(DEFAULT_APP_MODE))
        setTempGifticonFilterSetting(buildDefaultGifticonFilterSetting(DEFAULT_APP_MODE))
        setCoupangGifticonFilterSetting(buildDefaultGifticonFilterSetting(COUPANG_APP_MODE))
        setTempCoupangGifticonFilterSetting(buildDefaultGifticonFilterSetting(COUPANG_APP_MODE))
        setEditUrlMode(false)
        setEditGifticonFilterMode(false)
        setEditCoupangGifticonFilterMode(false)
        setHasDetail(false)
        setDetailErrorMessage(errorMessage)
    }, [])

    // 앱 기프티콘 목록 노출 금액 범위는 카테고리 관리 API에서 제공하는 설정값을 appMode별로 재사용한다.
    const fetchGifticonFilterSetting = useCallback(async (appMode = DEFAULT_APP_MODE) => {
        const { data } = await API.post('/api/v1/admin/appGifticonCategory/getList', {
            page: 1,
            offset: 1,
            searchText: '',
            searchSelect: { searchStatus: '' },
            appMode,
        })

        if (data.code === 200) {
            return data.data?.filterSetting || buildDefaultGifticonFilterSetting(appMode)
        }

        if (data.code === 403) {
            history.push('/login')
            return null
        }

        throw new Error(data.message || '앱 기프티콘 노출 설정을 불러오지 못했습니다.')
    }, [history])
    
    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
            setHasDetail(false)
        }
        setDetailErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/setting/getCoupangPartnersUrl', { })
            if (data.code === 200) {
                const detail = data.data || {}
                const hasSetting = Boolean(detail?.coupangPartnersUrlId)

                if (!hasSetting) {
                    resetDetailState('설정 정보를 찾을 수 없습니다.')
                    return
                }

                const [defaultGifticonSetting, coupangGifticonSetting] = await Promise.all([
                    fetchGifticonFilterSetting(DEFAULT_APP_MODE),
                    fetchGifticonFilterSetting(COUPANG_APP_MODE),
                ])

                if (!defaultGifticonSetting || !coupangGifticonSetting) {
                    return
                }

                setParams(detail)
                setTempParams(detail)
                setGifticonFilterSetting(defaultGifticonSetting)
                setTempGifticonFilterSetting(defaultGifticonSetting)
                setCoupangGifticonFilterSetting(coupangGifticonSetting)
                setTempCoupangGifticonFilterSetting(coupangGifticonSetting)
                setHasDetail(hasSetting)
                setDetailErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '설정 정보를 불러오지 못했습니다.'
                resetDetailState(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (error) {
            const errorMessage = error.message || '설정 정보를 불러오지 못했습니다.'
            resetDetailState(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [fetchGifticonFilterSetting, history, resetDetailState])

    useEffect(() => {
        getData()
    }, [getData])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    // 설정 화면은 단일 운영 설정이므로 오류 상태에서도 다시 조회 액션을 제공한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="설정 정보를 불러오지 못했습니다."
            description={detailErrorMessage || '잠시 후 다시 시도해주세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" onBackClick={() => getData()} backLabel="다시 시도" actionColor="primary" />
            )}
            sx={{ mt: 2, mb: 2 }}
        />
    )

    const handleUpdateField = (e) => {
        setParams({
            ...params,
            [e.target.name]: e.target.value
        })
    }

    const handleUpdateGifticonFilterField = (appMode, e) => {
        const updateSetting = (prevSetting) => ({
            ...prevSetting,
            appMode,
            [e.target.name]: e.target.value,
        })

        if (appMode === COUPANG_APP_MODE) {
            setCoupangGifticonFilterSetting(updateSetting)
            return
        }

        setGifticonFilterSetting(updateSetting)
    }

    const handleEditClick = (field) => {
        if (saving || !hasDetail) {
            return
        }
        if (field === "url") setEditUrlMode(true)
        if (field === "gifticonFilter") setEditGifticonFilterMode(true)
        if (field === "coupangGifticonFilter") setEditCoupangGifticonFilterMode(true)
        setTempParams(params) // 현재 데이터 백업
        setTempGifticonFilterSetting(gifticonFilterSetting)
        setTempCoupangGifticonFilterSetting(coupangGifticonFilterSetting)
    }

    const handleCancelClick = (field) => {
        if (saving) {
            return
        }
        if (field === "url") setEditUrlMode(false)
        if (field === "gifticonFilter") setEditGifticonFilterMode(false)
        if (field === "coupangGifticonFilter") setEditCoupangGifticonFilterMode(false)
        setParams(tempParams) // 원래 데이터로 복원
        setGifticonFilterSetting(tempGifticonFilterSetting)
        setCoupangGifticonFilterSetting(tempCoupangGifticonFilterSetting)
    }

    const handleUpdate = async (field) => {
        if (saving) {
            return
        }

        if (field === "gifticonFilter") {
            await handleUpdateGifticonFilter(DEFAULT_APP_MODE)
            return
        }

        if (field === "coupangGifticonFilter") {
            await handleUpdateGifticonFilter(COUPANG_APP_MODE)
            return
        }

        if (field !== "url") {
            return
        }

        const nextUrl = (params.url || '').trim()

        if (!nextUrl) {
            setOpenSnackbar(true)
            setSeveritySnackbar('warning')
            setMessageSnackbar('쿠팡 URL을 입력해주세요.')
            return
        }

        try {
            setSaving(true)
            const { data } = await API.post('/api/v1/admin/setting/updateCoupangPartnersUrl', { coupangPartnersUrlId: params.coupangPartnersUrlId, url: nextUrl })
            if (data.code === 200) {
                setEditUrlMode(false)
                await getData(false)
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar(data.message)
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('설정을 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleUpdateGifticonFilter = async (appMode = DEFAULT_APP_MODE) => {
        const isCoupangMode = appMode === COUPANG_APP_MODE
        const currentSetting = isCoupangMode ? coupangGifticonFilterSetting : gifticonFilterSetting
        const minPrice = Number(currentSetting.minPrice)
        const maxPrice = Number(currentSetting.maxPrice)

        if (currentSetting.minPrice === '' || currentSetting.maxPrice === '' || !Number.isInteger(minPrice) || !Number.isInteger(maxPrice)) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('금액 범위를 숫자로 입력해주세요.')
            return
        }

        if (minPrice < 0 || maxPrice < 0) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('금액은 0원 이상으로 입력해주세요.')
            return
        }

        if (minPrice > maxPrice) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('최소 금액은 최대 금액보다 클 수 없습니다.')
            return
        }

        try {
            setSaving(true)
            const { data } = await API.post('/api/v1/admin/appGifticonCategory/filterSettingUpdate', { appMode, minPrice, maxPrice })
            if (data.code === 200) {
                const nextSetting = data.data?.filterSetting || { ...currentSetting, appMode, minPrice, maxPrice }
                if (isCoupangMode) {
                    setEditCoupangGifticonFilterMode(false)
                    setCoupangGifticonFilterSetting(nextSetting)
                    setTempCoupangGifticonFilterSetting(nextSetting)
                } else {
                    setEditGifticonFilterMode(false)
                    setGifticonFilterSetting(nextSetting)
                    setTempGifticonFilterSetting(nextSetting)
                }
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar(isCoupangMode ? '쿠팡 기업모드 기프티콘 노출 금액 범위를 저장했습니다.' : '앱 기프티콘 노출 금액 범위를 저장했습니다.')
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(isCoupangMode ? '쿠팡 기업모드 기프티콘 노출 설정을 저장하지 못했습니다.' : '앱 기프티콘 노출 설정을 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const renderGifticonFilterSettingSection = ({ appMode, title, description, setting, editMode, field }) => (
        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e5e7eb', opacity: saving ? 0.6 : 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                {description}
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                        <TextField
                            InputProps={{ readOnly: !editMode || saving }}
                            disabled={saving}
                            label="최소 공급가"
                            required
                            id={`${appMode}-minPrice`}
                            name="minPrice"
                            type="number"
                            value={setting.minPrice}
                            onChange={(e) => handleUpdateGifticonFilterField(appMode, e)}
                            size="small"
                            inputProps={{ min: 0 }}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                        <TextField
                            InputProps={{ readOnly: !editMode || saving }}
                            disabled={saving}
                            label="최대 공급가"
                            required
                            id={`${appMode}-maxPrice`}
                            name="maxPrice"
                            type="number"
                            value={setting.maxPrice}
                            onChange={(e) => handleUpdateGifticonFilterField(appMode, e)}
                            size="small"
                            inputProps={{ min: 0 }}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Box display="flex" flexDirection="row" alignItems="center" justifyContent="flex-end" gap={1}>
                        {!editMode && (
                            <Button
                                variant="contained"
                                color="success"
                                sx={FORM_INLINE_ACTION_BUTTON_SX}
                                onClick={() => handleEditClick(field)}
                                disabled={saving}
                            >
                                수정
                            </Button>
                        )}
                        {editMode && (
                            <>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    sx={FORM_INLINE_ACTION_BUTTON_SX}
                                    onClick={() => handleCancelClick(field)}
                                    disabled={saving}
                                >
                                    취소
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    sx={{ ...FORM_INLINE_ACTION_BUTTON_SX, minWidth: 88 }}
                                    onClick={() => handleUpdate(field)}
                                    disabled={saving}
                                    startIcon={saving ? BUTTON_LOADING_ICON : undefined}
                                    aria-busy={saving}
                                >
                                    {saving ? '저장 중...' : '저장'}
                                </Button>
                            </>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    )

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : !hasDetail ? (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>설정</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>설정</Title>
                    
                    {/* URL 입력칸 */}
                    <Box sx={{ opacity: saving ? 0.6 : 1 }}>
                        <Grid container spacing={2} sx={{ marginTop: 2 }}>
                                <Grid item xs={12} md={9}>
                                    <FormControl fullWidth>
                                        <TextField
                                            InputProps={{ readOnly: !editUrlMode || saving }}
                                            disabled={saving}
                                            label="쿠팡 URL"
                                            required
                                            id="url"
                                            name="url"
                                            value={params.url || ''}
                                            onChange={handleUpdateField}
                                            size="small"
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Box display="flex" flexDirection="row" alignItems="center" justifyContent="flex-end" gap={1}>
                                        {!editUrlMode && (
                                            <Button
                                                variant="contained"
                                                color="success"
                                                sx={FORM_INLINE_ACTION_BUTTON_SX}
                                                onClick={() => handleEditClick('url')}
                                                disabled={saving}
                                            >
                                                수정
                                            </Button>
                                        )}
                                        {editUrlMode && (
                                            <>
                                                <Button
                                                    variant="contained"
                                                    color="secondary"
                                                    sx={FORM_INLINE_ACTION_BUTTON_SX}
                                                    onClick={() => handleCancelClick('url')}
                                                    disabled={saving}
                                                >
                                                    취소
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    sx={{ ...FORM_INLINE_ACTION_BUTTON_SX, minWidth: 88 }}
                                                    onClick={() => handleUpdate('url')}
                                                    disabled={saving}
                                                    startIcon={saving ? BUTTON_LOADING_ICON : undefined}
                                                    aria-busy={saving}
                                                >
                                                    {saving ? '저장 중...' : '저장'}
                                                </Button>
                                            </>
                                        )}
                                    </Box>
                                </Grid>
                        </Grid>
                    </Box>

                    {renderGifticonFilterSettingSection({
                        appMode: DEFAULT_APP_MODE,
                        title: '앱 기프티콘 노출 설정',
                        description: '앱 기프티콘 목록에 노출할 상품의 공급가 범위를 설정합니다.',
                        setting: gifticonFilterSetting,
                        editMode: editGifticonFilterMode,
                        field: 'gifticonFilter',
                    })}

                    {renderGifticonFilterSettingSection({
                        appMode: COUPANG_APP_MODE,
                        title: '쿠팡 기업모드 기프티콘 노출 설정',
                        description: '쿠팡 기업모드 앱 기프티콘 목록에 노출할 상품의 공급가 범위를 설정합니다.',
                        setting: coupangGifticonFilterSetting,
                        editMode: editCoupangGifticonFilterMode,
                        field: 'coupangGifticonFilter',
                    })}

                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
